import {Injectable} from '@angular/core';
import {SettingsService} from "@components/settings/settings.service";
import * as moment from 'moment';
import {Taxe} from "@domain/taxe/taxe";
import {Frais} from "@domain/depense/frais";
import {Taux} from "@domain/taxe/taux";
import {Prestation,TypePrestation} from "@domain/prestation/prestation";
import {SaisieTaxe} from "@domain/taxe/saisie-taxe";
import {NdfTaxe} from "@domain/taxe/ndf-taxe";

/**
 * Service de gestion de la taxe pour un frais
 * La portée du service est liée au composant
 *
 * @author Lionel Gimenez
 * @date 25/02/2025
 */
@Injectable()
export class FraisTaxeService {

    /** Indique si on peut modifier le montant de la taxe */
    isPrestationModifierTaxe: boolean = false;

    /** La taxe en cours */
    taxe: Taxe;

    /**
     * Le constructeur
     */
    constructor(
        private settingsService: SettingsService,
    ) {
    }

    /**
     * Indique si on affiche la TVA
     */
    public isShowTaxe(): boolean {
        //Si on a au moins une taxe
        return this.taxe?.listeTaux?.length >= 1;
    }

    /**
     * Indique si on a plusieurs taux
     */
    public isMultiTaux(): boolean {
        //Si on a au moins deux taxes
        return this.taxe?.listeTaux?.length >= 2;
    }

    /**
     * Indique si on peut modifier la TVA
     */
    public canModifierMontantTaxe(): boolean {
        //Si sur la prestation on peut modifier la taxe ou si on est en multi-taux
        return this.isPrestationModifierTaxe || this.isMultiTaux();
    }

    /**
     * Initialise les montants des taxes si null
     */
    public initTaxeMontantsIfNull(frais: Frais): void {
        frais?.saisieTaxe?.listeTaxes?.forEach((ndfTaxe: NdfTaxe) => {
            ndfTaxe.montant = ndfTaxe.montant ?? 0.0;
            ndfTaxe.montantBase = ndfTaxe.montantBase ?? 0.0;
            ndfTaxe.montantHt = ndfTaxe.montantHt ?? 0.0;
            ndfTaxe.montantHtBase = ndfTaxe.montantHtBase ?? 0.0;
            ndfTaxe.montantTtc = ndfTaxe.montantTtc ?? 0.0;
            ndfTaxe.montantTtcBase = ndfTaxe.montantTtcBase ?? 0.0;
        });
    }

    /**
     * Reset à 0 les montants des taxes
     */
    public resetTaxeMontants(frais: Frais): void {
        frais?.saisieTaxe?.listeTaxes?.forEach((ndfTaxe: NdfTaxe) => {
            ndfTaxe.montant = 0.0;
            ndfTaxe.montantBase = 0.0;
            ndfTaxe.montantHt = 0.0;
            ndfTaxe.montantHtBase = 0.0;
            ndfTaxe.montantTtc = 0.0;
            ndfTaxe.montantTtcBase = 0.0;
        });
    }

    /**
     * Retourne la taxe principale à partir du taux
     */
    public getTaxePrincipal(): Taux {
        const listeTaux = this.taxe.listeTaux;

        //Si on n'a pas de taux
        if (listeTaux == null || listeTaux.length == 0) {
            return null;
        } else if (listeTaux.length == 1) {
            //Si on a un qu'un seul taux
            return listeTaux[0];
        } else {
            //On renvoie le taux défini par défaut
            return listeTaux.find((taux: Taux) => taux.defaut == true);
        }
    }

    /**
     * Rafraichissement de la taxe liée à la prestation
     * Et mise à jour de saisieTaxe sur le frais qui permet d'enregistrer la saisie
     */
    public refreshTaxeForPrestation(frais: Frais) {
        //Vérification de la prestation
        if (frais.prestation?.idAppTva && frais.geographie?.pays?.idPays) {
            //Recherche de la taxe
            this.settingsService.findTaxeForVilleAndPays({
                idDomaine: frais.prestation.idAppTva,
                idPays: frais.geographie.pays.idPays,
                idVille: frais.geographie.ville?.idVille,
                date: frais.date || moment().toDate()
            }).subscribe({
                next: (taxe: Taxe) => {
                    //On enregistre la taxe sur le service
                    this.taxe = taxe;

                    //On initialise la saisie
                    const saisieTaxe: SaisieTaxe = new SaisieTaxe();

                    //On trie par défaut puis par ordre, l'affichage va suivre cet ordre
                    taxe.listeTaux?.sort((a,b) => {
                        //On trie par défaut
                        if (a.defaut && !b.defaut) {
                            return -1
                        }

                        if (!a.defaut && b.defaut) {
                            return 1
                        }

                        //Par ordre
                        return a.ordre - b.ordre
                    });

                    //Vérification de la présence de taxe sur la dépense
                    if (taxe.listeTaux?.length) {
                        //Pour chaque taux de la taxe
                        taxe.listeTaux.forEach((taux: Taux) => {
                            //On cherche la saisie sur le frais pour la récupérer
                            let ndfTaxe: NdfTaxe = frais.listeTaxes?.find((ndfTaxe: NdfTaxe) => ndfTaxe.idTaux == taux.idTaux);

                            //Si on a pas de saisie
                            if (ndfTaxe == null) {
                                //On crée une saisie
                                ndfTaxe = {
                                    idSaisie: 0,
                                    idDepense: frais.idDepense,
                                    idTaux: taux.idTaux,
                                    taux: taux.taux,
                                    montant: 0,
                                    montantBase: 0,
                                    montantHt: 0,
                                    montantHtBase: 0,
                                    montantTtc: 0,
                                    montantTtcBase: 0,
                                };
                            }

                            //On enregistre la taxe sur la saisie
                            saisieTaxe.listeTaxes.push(ndfTaxe);
                        });
                    }

                    //Mise à jour de la taxe sur le frais
                    frais.saisieTaxe = saisieTaxe;

                    //Si le paramétrage de la prestation permet de modifier le montant de la taxe
                    this.isPrestationModifierTaxe = frais.prestation?.saisieTaxe;
                }
            });
        } else {
            //Retrait de la taxe
            frais.saisieTaxe = null;
            this.taxe = null;
            this.isPrestationModifierTaxe = false;
        }
    }

    /**
     * Retourne le nom d'un taux
     * @param idTaux l'id du taux
     */
    getTauxNom(idTaux: number) {
        return this.taxe.listeTaux.find(t => t.idTaux == idTaux).nom;
    }

    /**
     * Vérification de la taxe saisie
     */
    isTaxeValid(montant: number,listeNdfTaxe: NdfTaxe[]): boolean {
        //On s'assure que le montant n'est pas null
        montant = montant ?? 0;

        //Si on peut saisir la taxe vérification de la présence d'une taxe et d'une saisie
        if (this.isShowTaxe() && this.canModifierMontantTaxe() && this.taxe && listeNdfTaxe?.length > 0) {
            //Vérification du signe des montants saisis
            if (listeNdfTaxe.filter(t => t.montantBase).some(t => t.montantBase >= 0 && montant < 0)) {
                return false;
            }

            //Si un montant saisi est négatif
            if (listeNdfTaxe.some(t => t.montantBase < 0)) {
                return false;
            }

            //Si un montant saisi est null
            if (listeNdfTaxe.some((ndfTaxe: NdfTaxe) => ndfTaxe.montantBase == null)) {
                return false;
            }

            //Calcul de la somme
            let somme = listeNdfTaxe.filter(t => t.montantBase).reduce((somme,taux) => {
                //On corrige la valeur si on a une virgule
                const montantBase = Number(taux.montantBase.toString().replace(",","."));
                return somme + Number(montantBase + 100 * (Math.abs(montantBase) - 0.01) / taux.taux);
            },0);

            //Vérification de la validité de la somme
            return somme <= Math.abs(montant) && somme >= 0;
        }

        return true;
    }

    /**
     * Calcul de la taxe
     */
    public calculerTaxe(frais: Frais): void {
        const prestation: Prestation = frais.prestation;
        const montantRemboursable = frais.montantRemboursable; // voir pour forfait si toujours bon montant
        const montantDepense = frais.montant;
        const tauxPrincipal = this.getTaxePrincipal();

        let taxeRemb: number;
        let taxeBase: number;

        //Initialisation
        let taux = 0.0;

        //Vérification de la sélection du taux
        taux = tauxPrincipal != null ? tauxPrincipal.taux / 100.0 : 0.0;

        //Présence d'un justificatif et de la taxe
        let isJustificatif = frais.isJustificatif;

        //Calcul de la taxe primaire
        if (taux != 0.0 && isJustificatif && prestation.idAppTva > 0) {
            //Ecrétage du montant remboursable (quand saisie du montant dépensé)
            if (prestation.type == TypePrestation.FORFAIT && prestation.idAppTva > 0 && montantRemboursable > montantDepense) {
                //Calcul de la taxe
                taxeRemb = montantDepense * (1 - 1 / (1 + taux));
                taxeBase = montantDepense * (1 - 1 / (1 + taux));
            } else {
                //Calcul de la taxe
                taxeRemb = montantRemboursable * (1 - 1 / (1 + taux));
                taxeBase = montantDepense * (1 - 1 / (1 + taux));
            }

            //Définition de la taxe
            if (this.taxe != null && this.taxe.listeTaux && this.taxe.listeTaux.length > 0) {
                //Parcours des taxes
                for (const taux of this.taxe.listeTaux) {
                    //Taxe courante
                    const currentTauxSaisie = frais.saisieTaxe.listeTaxes.find(s => s.idTaux == taux.idTaux);

                    //Vérification du taux courant
                    if (currentTauxSaisie.idTaux != tauxPrincipal.idTaux) {
                        //Définition des montants
                        currentTauxSaisie.montant = 0.0;
                        currentTauxSaisie.montantBase = 0.0;
                    } else {
                        //Définition des montants calculés
                        currentTauxSaisie.montant = parseFloat(taxeRemb.toFixed(2));
                        currentTauxSaisie.montantBase = parseFloat(taxeBase.toFixed(2));
                        currentTauxSaisie.montantTtc = parseFloat(montantRemboursable?.toFixed(2));
                        currentTauxSaisie.montantTtcBase = montantDepense;
                        currentTauxSaisie.montantHt = currentTauxSaisie.montantTtc - currentTauxSaisie.montant;
                        currentTauxSaisie.montantHtBase = currentTauxSaisie.montantTtcBase - currentTauxSaisie.montantBase;
                    }
                }
            }
        } else if (this.taxe?.listeTaux?.length > 0) {
            //Aucune Taxe
            //Parcours des taxes
            for (const taux of this.taxe.listeTaux) {
                //Taxe courante
                const currentTauxSaisie = frais.saisieTaxe.listeTaxes.find(s => s.idTaux == taux.idTaux);

                //Définition des montants
                currentTauxSaisie.montant = 0.0;
                currentTauxSaisie.montantBase = 0.0;
            }
        }
    }
}
